import React, { useEffect, useState } from "react";
import Table from "../components/Table";
import axios from "axios";
import { apiKey } from "../config/config";
import DatePicker from "react-multi-date-picker";

export default function InstallmentReport({ lang, path, heading }) {
  const token = JSON.parse(sessionStorage.getItem("aman-admin") || "null")
    ?.admin
    ? JSON.parse(JSON.parse(sessionStorage.getItem("aman-admin")).admin).token
    : null;

  const [contractList, setContractList] = useState([]);
  const [futureInstallments, setFutureInstallments] = useState([]);
  const [branchId, setBranchId] = useState();
  const [filter, setFilter] = useState("");
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [investorList, setInvestorList] = useState([]);
  const [investor, setInvestor] = useState("");
  const [status, setStatus] = useState("1"); // 1 = in progress
  const [reportView, setReportView] = useState("contracts"); // "contracts" or "breakdown"
  const [selectedContract, setSelectedContract] = useState(null);

  function convertDateFormat(inputDate) {
    if (!inputDate) return "";
    const dateObject = new Date(inputDate);
    if (Number.isNaN(dateObject.getTime())) return "";
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    return dateObject.toLocaleDateString("en-GB", options);
  }

  const formatYMD = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().split("T")[0];
  };

  // Columns for Contracts Summary view
  const contractColumns = [
    {
      header: "SN",
      cell: (row) => row.row.index + 1,
      footer: (info) => lang["Total"] || "Total",
    },
    {
      header: lang.reference_number || "Reference Number",
      accessorKey: "contract_reference",
      cell: (row) => row.getValue(),
    },
    {
      header: lang.Clients || "Clients",
      accessorKey: "customer_name",
    },
    {
      header: lang.Investors || "Investors",
      accessorKey: "investor_name",
      cell: (row) => row.getValue() || "-",
    },
    {
      header: lang.Amount || "Amount",
      accessorKey: "total_value",
      cell: (row) => Number(row.getValue() || 0).toFixed(2),
      footer: (info) => {
        const total = info.table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + Number(row.original.total_value || 0), 0);
        return `${total.toFixed(2)}`;
      },
    },
    {
      header: lang.Paid_Up || "Paid up",
      accessorKey: "total_paid",
      cell: (row) => Number(row.getValue() || 0).toFixed(2),
      footer: (info) => {
        const total = info.table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + Number(row.original.total_paid || 0), 0);
        return `${total.toFixed(2)}`;
      },
    },
    {
      header: lang.Residual || "Residual",
      accessorKey: "residual",
      cell: (row) => Number(row.getValue() || 0).toFixed(2),
      footer: (info) => {
        const total = info.table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + Number(row.original.residual || 0), 0);
        return `${total.toFixed(2)}`;
      },
    },
    {
      header: lang.installment_amount || "Installment Amount",
      accessorKey: "premium",
      cell: (row) => Number(row.getValue() || 0).toFixed(2),
    },
    {
      header: lang.Installments || "Installments",
      cell: (row) =>
        `${row.row.original.paid_installments_count || 0} / ${
          row.row.original.no_of_installments || 0
        }`,
    },
    {
      header: lang.due_date || "Due Date",
      accessorKey: "next_due_date",
      cell: (row) => convertDateFormat(row.getValue()),
    },
    {
      header: lang.Status || "Status",
      accessorKey: "status",
      cell: (row) => {
        const st = Number(row.getValue());
        return (
          <span
            className={`badge rounded-pill ${
              st === 1 ? "bg-success" : st === 2 ? "bg-primary" : "bg-danger"
            }`}
          >
            {st === 1
              ? lang.in_progress || "in-progress"
              : st === 2
              ? lang.completed || "Completed"
              : st === 3
              ? lang.defaulted || "Defaulted"
              : ""}
          </span>
        );
      },
    },
    {
      header: lang.Actions || "Actions",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => setSelectedContract(row.row.original)}
        >
          {lang.Breakdown || "Breakdown"}
        </button>
      ),
    },
  ];

  // Columns for All Installments Breakdown view
  const installmentColumns = [
    {
      header: "SN",
      cell: (row) => row.row.index + 1,
      footer: (info) => lang["Total"] || "Total",
    },
    {
      header: lang.reference_number || "Reference Number",
      accessorKey: "contract_reference",
      cell: (row) => row.getValue(),
    },
    {
      header: lang.Clients || "Clients",
      accessorKey: "customer_name",
    },
    {
      header: lang.Investors || "Investors",
      accessorKey: "investor_name",
      cell: (row) => row.getValue() || "-",
    },
    {
      header: lang.installment_no || "Installment #",
      cell: (row) =>
        `#${row.row.original.installment_no} / ${row.row.original.total_installments}`,
    },
    {
      header: lang.due_date || "Due Date",
      accessorKey: "due_date",
      cell: (row) => convertDateFormat(row.getValue()),
    },
    {
      header: lang.Amount || "Amount",
      accessorKey: "amount",
      cell: (row) => Number(row.getValue() || 0).toFixed(2),
      footer: (info) => {
        const total = info.table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + Number(row.original.amount || 0), 0);
        return `${total.toFixed(2)}`;
      },
    },
    {
      header: lang.Status || "Status",
      accessorKey: "status",
      cell: (row) => {
        const st = row.getValue();
        let badgeClass = "bg-info";
        let label = lang.upcoming || "Upcoming";

        if (st === "overdue") {
          badgeClass = "bg-danger";
          label = lang.overdue || "Overdue";
        } else if (st === "due_this_month") {
          badgeClass = "bg-warning text-dark";
          label = lang.due_this_month || "Due This Month";
        } else if (st === "paid") {
          badgeClass = "bg-success";
          label = lang.paid || "Paid";
        }

        return <span className={`badge rounded-pill ${badgeClass}`}>{label}</span>;
      },
    },
  ];

  useEffect(() => {
    if (filter !== "from_to") {
      setFrom(null);
      setTo(null);
    }
  }, [filter]);

  useEffect(() => {
    const adminObj = JSON.parse(
      JSON.parse(sessionStorage.getItem("aman-admin") || "{}")?.admin || "{}"
    );
    setBranchId(adminObj.branch_id);
  }, []);

  useEffect(() => {
    if (!branchId || !token) return;

    axios
      .get(apiKey + "/investor", {
        params: { branch_id: branchId },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setInvestorList(res.data.result || []);
      })
      .catch((err) => console.error(err));
  }, [branchId, token]);

  useEffect(() => {
    if (!branchId || !token) return;

    axios
      .get(apiKey + "/installment-report", {
        params: {
          branch_id: branchId,
          filter,
          from_date: filter === "from_to" ? formatYMD(from) : "",
          to_date: filter === "from_to" ? formatYMD(to) : "",
          investor_id: investor,
          status: status,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setContractList(res.data.contracts || []);
        setFutureInstallments(res.data.future_installments || []);
      })
      .catch((err) => console.error(err));
  }, [branchId, filter, from, to, investor, status, token]);

  return (
    <div>
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>{lang.Reports || "Reports"}</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                >
                  <path d="m21.743 12.331-9-10c-.379-.422-1.107-.422-1.486 0l-9 10a.998.998 0 0 0-.17 1.076c.16.361.518.593.913.593h2v7a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-4h4v4a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-7h2a.998.998 0 0 0 .743-1.669z"></path>
                </svg>
              </li>
              <li className="breadcrumb-item">{lang.Reports || "Reports"}</li>
              <li className="breadcrumb-item active">
                {path || lang.Installment_Report || "Installment Report"}
              </li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="mt-3 mb-3">
                    <div className="rr">
                      {heading || lang.Installment_Report || "Installment Report"}
                    </div>

                    <div className="row justify-content-end gap-2">
                      {filter === "from_to" && (
                        <>
                          <div className="custom-datepicker">
                            <DatePicker
                              className="form-control w-100"
                              placeholder={lang["from_text"] || "From"}
                              style={{
                                left: "35%",
                                top: "5px",
                                height: "auto",
                              }}
                              containerStyle={{
                                height: "auto",
                              }}
                              onChange={(e) => {
                                const dateObject = new Date(e);
                                setFrom(dateObject);
                              }}
                            />
                          </div>
                          <div className="custom-datepicker">
                            <DatePicker
                              className="form-control w-100"
                              placeholder={lang["to_text"] || "To"}
                              style={{
                                left: "45%",
                                top: "5px",
                                height: "auto",
                              }}
                              containerStyle={{
                                height: "auto",
                              }}
                              onChange={(e) => {
                                const dateObject = new Date(e);
                                setTo(dateObject);
                              }}
                            />
                          </div>
                        </>
                      )}
                      <select
                        onChange={(e) => setReportView(e.target.value)}
                        value={reportView}
                        className="form-data col-2 btn-export"
                      >
                        <option value="contracts">
                          {lang.contracts_view || "Contracts Summary"}
                        </option>
                        <option value="breakdown">
                          {lang.installments_view || "All Installments Breakdown"}
                        </option>
                      </select>
                      <select
                        onChange={(e) => setStatus(e.target.value)}
                        value={status}
                        className="form-data col-2 btn-export"
                      >
                        <option value="1">
                          {lang.in_progress || "In Progress"}
                        </option>
                        <option value="3">
                          {lang.defaulted || "Defaulted"}
                        </option>
                        <option value="all">
                          {lang.all_contracts || "All Statuses"}
                        </option>
                      </select>
                      <select
                        onChange={(e) => setInvestor(e.target.value)}
                        value={investor}
                        className="form-data col-2 btn-export"
                        style={{ top: "48px" }}
                      >
                        <option value="">
                          {lang.all_investors || "All Investors"}
                        </option>
                        {investorList.map((inv, index) => (
                          <option key={index} value={inv.id}>
                            {inv.FullName}
                          </option>
                        ))}
                      </select>
                      <select
                        onChange={(e) => setFilter(e.target.value)}
                        value={filter}
                        className="form-data btn-export col-2"
                      >
                        <option value="">{lang.All_Time || "All Time"}</option>
                        <option value="today">{lang.Today || "Today"}</option>
                        <option value="this_week">
                          {lang.This_Week || "This Week"}
                        </option>
                        <option value="this_month">
                          {lang.This_Month || "This Month"}
                        </option>
                        <option value="this_year">
                          {lang.This_Year || "This Year"}
                        </option>
                        <option value="from_to">
                          {lang["From To"] || "From To"}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="d-flex mt-3 mb-3">
                    <Table
                      lang={lang}
                      data={reportView === "contracts" ? contractList : futureInstallments}
                      columns={reportView === "contracts" ? contractColumns : installmentColumns}
                      footer={true}
                      exportOption={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Breakdown Modal */}
      {selectedContract && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  {lang.installment_breakdown || "Installment Breakdown"} - {lang.reference_number || "Ref"} #{selectedContract.contract_reference}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedContract(null)}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row bg-light p-3 rounded mb-3 text-center">
                  <div className="col-3">
                    <div className="text-muted small">{lang.Clients || "Customer"}</div>
                    <div className="fw-bold">{selectedContract.customer_name}</div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted small">{lang.Amount || "Amount"}</div>
                    <div className="fw-bold">{Number(selectedContract.total_value).toFixed(2)}</div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted small">{lang.Paid_Up || "Paid Up"}</div>
                    <div className="fw-bold text-success">{Number(selectedContract.total_paid).toFixed(2)}</div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted small">{lang.Residual || "Residual"}</div>
                    <div className="fw-bold text-primary">{Number(selectedContract.residual).toFixed(2)}</div>
                  </div>
                </div>

                <div className="table-responsive" style={{ maxHeight: "350px" }}>
                  <table className="table table-bordered table-striped text-center align-middle mb-0">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th>#</th>
                        <th>{lang.due_date || "Due Date"}</th>
                        <th>{lang.installment_amount || "Amount"}</th>
                        <th>{lang.Status || "Status"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedContract.installments_schedule || []).map((inst) => (
                        <tr key={inst.installment_no}>
                          <td>#{inst.installment_no}</td>
                          <td>{convertDateFormat(inst.due_date)}</td>
                          <td className="fw-bold">{Number(inst.amount).toFixed(2)}</td>
                          <td>
                            {inst.is_paid ? (
                              <span className="badge bg-success">
                                {lang.paid || "Paid"}
                              </span>
                            ) : inst.status === "overdue" ? (
                              <span className="badge bg-danger">
                                {lang.overdue || "Overdue"}
                              </span>
                            ) : inst.status === "due_this_month" ? (
                              <span className="badge bg-warning text-dark">
                                {lang.due_this_month || "Due This Month"}
                              </span>
                            ) : (
                              <span className="badge bg-info">
                                {lang.upcoming || "Upcoming"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedContract(null)}
                >
                  {lang.Close || "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
